// src/Components/DeviceDashboard.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation, Navigate } from 'react-router-dom';
import api from '../utils/api';                            // ✅ default import로 교체
import DeviceControlModal from '../Components/DeviceControlModal';
import '../css/DeviceDashboard.css';
import DeviceAddModal from '../Components/DeviceAddModal';

const API_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/+$/, '');

export default function DeviceDashboard() {
  const { locationName, floorName } = useParams();
  const { state } = useLocation();
  const locationFromState = state?.location || null;

  const [location, setLocation] = useState(locationFromState);
  const [deviceList, setDeviceList] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [newDevice, setNewDevice] = useState({ id: '', memo: '', x: null, y: null });

  const itemsPerPage = 10;

  // 🔸 (삭제됨) 진입 시 토큰 선검사: 401은 axios 인터셉터가 자동으로 refresh/로그아웃 처리
  // useEffect(() => { ... }, []);

  // 4) location 정보 로딩
  useEffect(() => {
    let cancelled = false;
    if (location) return;
    if (!locationName) return;

    (async () => {
      try {
        const res = await api.get('/location', { params: { name: locationName } });
        if (!cancelled) setLocation(res.data || null);
      } catch (e) {
        console.error('로케이션 로딩 실패', e);
      }
    })();

    return () => { cancelled = true; };
  }, [location, locationName]);

  // 5) 선택된 층 계산
  const selectedFloor = useMemo(() => {
    if (!location?.floors || location.floors.length === 0) return null;
    if (floorName) {
      const found = location.floors.find(f => f.name === floorName);
      if (!found) {
        console.warn(`[DeviceDashboard] floorName='${floorName}'을(를) 찾지 못해 첫 층을 사용합니다.`);
      }
      return found || location.floors[0];
    }
    return location.floors[0];
  }, [location?.floors, floorName]);

  // 6) 디바이스 폴링
  useEffect(() => {
    if (!location?.name) return;
    if (!selectedFloor?.name) return;

    let cancelled = false;
    const fetchList = () =>
      api.get('/devices', {
        params: { location: location.name, floor: selectedFloor.name }
      })
        .then(res => { if (!cancelled) setDeviceList(Array.isArray(res.data) ? res.data : []); })
        .catch(err => console.error('장치 목록 폴링 실패', err));

    fetchList();
    const id = setInterval(fetchList, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [location?.name, selectedFloor?.name]);

  // 7) 검색/페이지네이션
  const filteredDevices = useMemo(() =>
    deviceList.filter(d => (d?.id || '').toLowerCase().includes(searchTerm.toLowerCase()))
  , [deviceList, searchTerm]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedDevices = filteredDevices.slice(startIndex, startIndex + itemsPerPage);
  const totalPages = Math.ceil(filteredDevices.length / itemsPerPage);

  // 8) 층 도면 URL
  const floorplanUrl = useMemo(() => {
    const url = selectedFloor?.floorplanUrl;
    if (!url) return '';
    return url.startsWith('http') ? url : `${API_BASE}${url}`;
  }, [selectedFloor]);

  // 9) 장비 등록
  const handleAddDevice = () => {
    if (!newDevice.id.trim()) {
      alert('디바이스 ID를 입력해주세요.');
      return;
    }
    if (!location?.name || !selectedFloor?.name) {
      alert('위치/층 정보가 없습니다.');
      return;
    }

    api.post('/device', {
      id: newDevice.id,
      location: location.name,
      floor: selectedFloor.name,
      memo: newDevice.memo,
      x: newDevice.x,
      y: newDevice.y,
      installedDirection:newDevice.installedDirection
    })
      .then(() => {
        alert('장비가 등록되었습니다.');
        setNewDevice({ id: '', memo: '', x: null, y: null });
        setShowAddModal(false);
        setCurrentPage(1);
        return api.get('/devices', {
          params: { location: location.name, floor: selectedFloor.name }
        }).then(res => setDeviceList(Array.isArray(res.data) ? res.data : []));
      })
      .catch((err) => {
        console.error('장비 등록 실패:', err);
        alert('❌ 장비 등록 실패');
      });
  };

  // 10) 선택 삭제
  const handleBulkDelete = async () => {
    if (selectedDeviceIds.length === 0) return;
    if (!window.confirm(`총 ${selectedDeviceIds.length}개의 디바이스를 삭제하시겠습니까?`)) return;
    try {
      await Promise.all(selectedDeviceIds.map((id) => api.patch(`/devices/${id}`)));
      setDeviceList((prev) => prev.filter((d) => !selectedDeviceIds.includes(d.id)));
      setSelectedDeviceIds([]);
      setSelectedDeviceId('');
      alert('선택 삭제 완료');
    } catch (error) {
      console.error('삭제 실패:', error);
      alert('삭제에 실패했습니다');
    }
  };

  if (!locationName && !location) {
    return <Navigate to="/locations" replace />;
  }
  if (!location) {
    return <div style={{ padding: '2rem' }}>위치 정보를 불러오는 중…</div>;
  }
  if (!selectedFloor) {
    return (
      <div style={{ padding: '2rem' }}>
        ❌ {location.name}에 등록된 층이 없습니다. 먼저 층을 추가해 주세요.
      </div>
    );
  }

  const titleText = `${location.name}${selectedFloor?.name ? ' — ' + selectedFloor.name : ''}`;

  return (
    <>
      {showModal && selectedDeviceId && (
        <DeviceControlModal
          deviceId={selectedDeviceId}
          onClose={() => setShowModal(false)}
        />
      )}

      <div className="dashboard-container">
        <h1 className="dashboard-title">장비 목록 - {titleText}</h1>

        <input
          type="text"
          placeholder="🔍 디바이스 ID 검색"
          className="device-search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <div className="device-count">
          {searchTerm
            ? `🔍 '${searchTerm}'에 대한 결과: 총 ${filteredDevices.length}개`
            : ` 총 ${filteredDevices.length}개의 장비가 등록되어 있습니다.`}
        </div>

        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
          <button
            className="delete-button"
            disabled={selectedDeviceIds.length === 0}
            onClick={handleBulkDelete}
          >
            🗑 선택 삭제
          </button>

          <button
            className="add-button"
            onClick={() => setShowAddModal(true)}
          >
            장비 등록
          </button>

          {showAddModal && (
            <DeviceAddModal
              onClose={() => setShowAddModal(false)}
              onSubmit={handleAddDevice}
              newDevice={newDevice}
              setNewDevice={setNewDevice}
              floorplanUrl={floorplanUrl}
              locationName={location.name}
              floorName={selectedFloor.name}   
            />
          )}
        </div>

        <div className="device-table-container">
          <div className="device-table-header">
            <div className="device-cell">장비명</div>
            <div className="device-cell">통신상태</div>
            <div className="device-cell">경보상태</div>
            <div className="device-cell">경보원인</div>
            <div className="device-cell">수집일시</div>
            <div className="device-cell">비고</div>
            <div className="device-cell">선택</div>
          </div>

          {paginatedDevices.length === 0 ? (
            <div className="device-table-row">
              <div className="device-cell" colSpan={7}>등록된 디바이스가 없습니다.</div>
            </div>
          ) : (
            paginatedDevices.map((device) => (
              <div
                key={device.id}
                className={`device-table-row ${selectedDeviceId === device.id ? 'selected' : ''}`}
                onClick={() => setSelectedDeviceId(device.id)}
                onDoubleClick={() => { setSelectedDeviceId(device.id); setShowModal(true); }}
              >
                <div className="device-cell">{device.id}</div>
                <div className="device-cell">
                  <span className={device.isConnected ? 'online' : 'offline'}>
                    {device.isConnected ? '정상' : '끊김'}
                  </span>
                </div>
                <div className={`device-cell ${device.motorFlag === 1 ? 'alert' : 'normal'}`}>
                  {device.motorFlag === 1 ? '⚠ 경보중' : '정상작동'}
                </div>
                <div className={`device-cell ${device.motorFlag === 1 ? 'alert' : 'normal'}`}>
                  {device.motorFlag === 1 ? (
                    [
                      device.smoke === 1 && '연기 감지',
                      device.blackout === 1 && '정전',
                      device.tempFlag === 1 && '온도 위험',
                      device.ffFacility === 1 && '접점신호 감지',
                      device.testButton === 0 && '테스트 버튼',
                      device.verkada===true && '버카다 환경센서',
                    ].filter(Boolean).join(', ') || '원격제어'
                  ) : '정상작동'}
                </div>
                <div className="device-cell">
                  {(() => {
                    const d = parseDeviceTs(device.timestamp);
                    return d ? (
                      <span className={freshnessClass(d)} title={String(device.timestamp)}>
                        {fmtKST.format(d)} <small>({timeAgo(d)})</small>
                      </span>
                    ) : '정보 없음';
                  })()}
                </div>
                <div className="device-cell">{device.memo}</div>
                <div className="device-cell">
                  <input
                    type="checkbox"
                    checked={selectedDeviceIds.includes(device.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedDeviceIds((prev) => [...prev, device.id]);
                      } else {
                        setSelectedDeviceIds((prev) => prev.filter((id) => id !== device.id));
                      }
                    }}
                  />
                </div>
              </div>
            ))
          )}
        </div>

        <div className="pagination">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            ◀ 이전
          </button>

          {[...Array(totalPages)].map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentPage(idx + 1)}
              className={currentPage === idx + 1 ? 'active' : ''}
            >
              {idx + 1}
            </button>
          ))}

          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            다음 ▶
          </button>
        </div>
      </div>
    </>
  );
}

/* ===== 시간/표시 유틸 ===== */
const fmtKST = new Intl.DateTimeFormat('ko-KR', {
  dateStyle: 'medium',
  timeStyle: 'medium',
  timeZone: 'Asia/Seoul',
});

function parseDeviceTs(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts === 'number') return new Date(ts < 1e12 ? ts * 1000 : ts);

  if (typeof ts === 'string') {
    const s = ts.trim();
    if (/[Zz]$/.test(s) || /[+-]\d\d:?\d\d$/.test(s) || /UTC/.test(s)) {
      const d = new Date(s);
      return isNaN(d) ? null : d;
    }
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) {
      const d = new Date(s.replace(' ', 'T') + '+09:00');
      return isNaN(d) ? null : d;
    }
    const d = new Date(s);
    return isNaN(d) ? null : d;
  }
  return null;
}

function timeAgo(d) {
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 30) return '방금 전';
  if (sec < 60) return `${sec}초 전`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}분 전`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  const day = Math.floor(hr / 24);
  return `${day}일 전`;
}

function freshnessClass(d) {
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  if (min <= 2) return 'ts-chip fresh';
  if (min <= 10) return 'ts-chip warm';
  return 'ts-chip stale';
}
